import { useState } from 'react';

import { HOLIDAY_CAMPAIGN } from '../config/campaign';

import { useHolidayStore } from '@/store/useHolidayStore';
import config from '@/config';

export function TweetVerificationModal() {
  const {
    showTweetModal,
    setShowTweetModal,
    setTweetVerified,
    verifyingTweet,
    setVerifyingTweet,
  } = useHolidayStore();

  const [tweetUrl, setTweetUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!showTweetModal) return null;

  const handleVerify = async () => {
    if (!tweetUrl.trim()) {
      setError('Please paste your tweet link');
      return;
    }

    // Basic URL validation for Twitter/X
    const tweetRegex = /^https?:\/\/(twitter\.com|x\.com)\/\w+\/status\/\d+/i;
    if (!tweetRegex.test(tweetUrl.trim())) {
      setError('Please enter a valid Twitter/X post link');
      return;
    }

    setError(null);
    setVerifyingTweet(true);

    try {
      // Use Holiday campaign endpoint for tweet verification
      // Pass the campaign config from frontend so we only need to update one place
      const response = await fetch(`${config.API_BASE_URL}/holiday/verify-tweet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          tweetUrl: tweetUrl.trim(),
          officialTweetId: HOLIDAY_CAMPAIGN.OFFICIAL_TWEET_ID,
          requiredMessage: HOLIDAY_CAMPAIGN.REQUIRED_MESSAGE,
        }),
      });

      const data = await response.json();

      if (data.success || data.data?.success || data.data?.verified || data.data?.alreadyVerified) {
        setTweetVerified(true);
        setShowTweetModal(false);
      } else {
        setError(data.data?.error || data.error || 'Tweet verification failed. Make sure you replied to the official tweet, included the secret phrase, and tagged 2+ friends.');
      }
    } catch (err) {
      setError('Failed to verify tweet. Please try again.');
    } finally {
      setVerifyingTweet(false);
    }
  };

  const handleSkip = () => {
    setShowTweetModal(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center pointer-events-none">
      {/* Backdrop - click to dismiss */}
      <div
        className="absolute inset-0 bg-black/50 pointer-events-auto cursor-pointer animate-modal-backdrop-in"
        onClick={handleSkip}
      />

      {/* Modal */}
      <div
        className="relative bg-gradient-to-b from-[#1a1a2e] to-[#16213e] rounded-xl p-4 max-w-xs w-auto mx-4 border border-orange-500/30 shadow-2xl animate-modal-backdrop-in font-outfit pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fire decoration */}
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-5xl">
          🔥
        </div>

        <h2 className="text-xl font-bold text-center text-orange-400 mt-4 mb-2">
          Verify Your Reply
        </h2>

        <p className="text-gray-300 text-center mb-4 text-sm">
          Paste the link to your reply tweet below to complete the ritual and claim your reward!
        </p>

        <div className="space-y-3">
          {/* Tweet requirements */}
          <div className="bg-black/30 rounded-lg p-2.5 text-sm">
            <p className="text-gray-400 mb-2">Your reply must:</p>
            <ul className="text-gray-300 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-0.5">✓</span>
                <span>
                  Reply to{' '}
                  <a
                    href={HOLIDAY_CAMPAIGN.OFFICIAL_TWEET_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-400 font-bold underline hover:text-orange-300"
                  >
                    this official tweet
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-0.5">✓</span>
                <span>
                  Include:{' '}
                  <span className="text-orange-400 font-bold italic">
                    "{HOLIDAY_CAMPAIGN.REQUIRED_MESSAGE}"
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 mt-0.5">✓</span>
                <span>Tag <span className="text-orange-400 font-bold">2+ friends</span></span>
              </li>
            </ul>
          </div>

          {/* Input */}
          <input
            type="url"
            value={tweetUrl}
            onChange={(e) => {
              setTweetUrl(e.target.value);
              setError(null);
            }}
            placeholder="Paste your reply tweet link here..."
            className="w-full bg-black/40 border border-gray-600 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
            disabled={verifyingTweet}
          />

          {/* Error message */}
          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSkip}
              className="flex-1 px-3 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors text-sm"
              disabled={verifyingTweet}
            >
              Skip
            </button>
            <button
              onClick={handleVerify}
              disabled={verifyingTweet}
              className="flex-1 px-3 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 text-white font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {verifyingTweet ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Verifying...
                </span>
              ) : (
                'Verify Tweet'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
